// Logik der Befehlspalette (ohne DOM): Filtern, Gruppieren, „Recent“, Suchanbieter, Kürzel.
import { type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

export interface PaletteCommand {
  id: string;
  /** People, Goals, Apps, Channels, Pages, Knowledge, Actions, Help, Chats … */
  group: string;
  label: string;
  description?: string;
  icon?: PhosphorIcon;
  leading?: ReactNode;
  keywords?: readonly string[];
  shortcut?: string;
  href?: string;
  /** Ziel liegt auf einer anderen Plattform (volle Navigation, Pfeil rechts). */
  reload?: boolean;
  perform?: () => void;
}

export interface PaletteResult {
  id: string;
  label: string;
  description?: string;
  href: string;
  icon?: PhosphorIcon;
  leading?: ReactNode;
  reload?: boolean;
}

export interface PaletteProvider {
  id: string;
  group: string;
  order: number;
  search: (query: string, signal: AbortSignal) => Promise<readonly PaletteResult[]>;
}

const normalize = (text: string) =>
  text.toLocaleLowerCase('en').normalize('NFKD').replace(/\p{M}/gu, '');

/** Treffer, wenn jedes Wort der Anfrage in Label, Beschreibung, Gruppe oder Schlüsselwörtern vorkommt. */
export function filterCommands(
  commands: readonly PaletteCommand[],
  query: string,
): PaletteCommand[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...commands];
  const scored = commands
    .map((command) => {
      const label = normalize(command.label);
      const haystack = normalize(
        [command.label, command.description ?? '', command.group, ...(command.keywords ?? [])].join(
          ' ',
        ),
      );
      if (!words.every((word) => haystack.includes(word))) return null;
      const score = label.startsWith(words[0]!) ? 0 : label.includes(words[0]!) ? 1 : 2;
      return { command, score };
    })
    .filter((entry): entry is { command: PaletteCommand; score: number } => entry !== null);
  return scored.sort((a, b) => a.score - b.score).map((entry) => entry.command);
}

/** Gruppiert unter Beibehaltung der Reihenfolge des ersten Auftretens. */
export function groupBy<T>(items: readonly T[], key: (item: T) => string): [string, T[]][] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    map.set(k, [...(map.get(k) ?? []), item]);
  }
  return [...map.entries()];
}

interface StoredRecent {
  id: string;
  group: string;
  label: string;
  description?: string;
  href: string;
  reload?: boolean;
}

export function readRecent(key: string): PaletteCommand[] {
  try {
    const raw = JSON.parse(localStorage.getItem(key) ?? '[]') as StoredRecent[];
    return Array.isArray(raw) ? raw.filter((r) => typeof r.href === 'string').slice(0, 5) : [];
  } catch {
    return [];
  }
}

/** Merkt sich nur Ziele mit Link (Aktionen ohne Link wären beim nächsten Mal tot). */
export function rememberRecent(
  key: string,
  item: {
    id: string;
    group: string;
    label: string;
    description?: string;
    href?: string;
    reload?: boolean;
  },
) {
  if (!item.href) return;
  try {
    const entry: StoredRecent = {
      id: item.id.startsWith(`${item.group}:`) ? item.id : `${item.group}:${item.id}`,
      group: item.group,
      label: item.label,
      description: item.description,
      href: item.href,
      reload: item.reload,
    };
    const next = [entry, ...readRecent(key).filter((r) => r.href !== item.href)].slice(0, 5);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // Speicher gesperrt
  }
}

export interface ProviderState {
  status: 'loading' | 'done' | 'error';
  items: readonly PaletteResult[];
}

/** Fragt alle Anbieter entprellt (120 ms) ab; alte Anfragen werden abgebrochen. */
export function useProviderResults(
  providers: readonly PaletteProvider[],
  query: string,
  debounceMs = 120,
): Record<string, ProviderState> {
  // Ergebnisse gelten nur für die Anfrage, zu der sie gehören; alles andere zählt als „lädt“.
  const [answered, setAnswered] = useState<{ query: string; byId: Record<string, ProviderState> }>({
    query: '',
    byId: {},
  });
  const trimmed = query.trim();
  useEffect(() => {
    if (!trimmed || providers.length === 0) return;
    const controller = new AbortController();
    const settle = (id: string, state: ProviderState) => {
      if (controller.signal.aborted) return;
      setAnswered((prev) => ({
        query: trimmed,
        byId: { ...(prev.query === trimmed ? prev.byId : {}), [id]: state },
      }));
    };
    const timer = setTimeout(() => {
      for (const provider of providers) {
        provider
          .search(trimmed, controller.signal)
          .then((items) => settle(provider.id, { status: 'done', items }))
          .catch(() => settle(provider.id, { status: 'error', items: [] }));
      }
    }, debounceMs);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [providers, trimmed, debounceMs]);
  return useMemo(() => {
    if (!trimmed) return {};
    const current = answered.query === trimmed ? answered.byId : {};
    return Object.fromEntries(
      providers.map((p) => [p.id, current[p.id] ?? { status: 'loading' as const, items: [] }]),
    );
  }, [answered, providers, trimmed]);
}

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/** ⌘K / Ctrl K öffnen immer; `/` nur außerhalb von Feldern und nur mit `singleKey` (K11). */
export function useCommandPaletteShortcut(onOpen: () => void, singleKey = true) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpen();
      } else if (
        singleKey &&
        event.key === '/' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !isTyping(event.target)
      ) {
        event.preventDefault();
        onOpen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpen, singleKey]);
}
